import { DEFAULT_TTS_LOCALE } from "../shared/locales";
import type { VoiceGroup } from "./types";

export const SUPPORTED_UI_LOCALES = ["zh-CN", "en-US", "ja-JP", "ko-KR"] as const;

export type UiLocale = (typeof SUPPORTED_UI_LOCALES)[number];

export const DEFAULT_UI_LOCALE: UiLocale = "zh-CN";

export const UI_LANGUAGE_LABELS: Record<UiLocale, string> = {
  "zh-CN": "中文",
  "en-US": "English",
  "ja-JP": "日本語",
  "ko-KR": "한국어",
};

export interface UiMessages {
  appTitle: string;
  themeLight: string;
  themeDark: string;
  textPlaceholder: string;
  settingsTitle: string;
  interfaceLanguage: string;
  ttsLanguage: string;
  voiceModel: string;
  speed: string;
  pitch: string;
  volume: string;
  generateTitle: string;
  generateCopy: string;
  generateButton: string;
  processingButton: string;
  resultTitle: string;
  idleStatus: string;
  runningStatus: string;
  successStatus: string;
  errorStatus: string;
  progressTitle: string;
  idleProgress: string;
  waitingForText: string;
  generatingAudio: string;
  completeProgress: string;
  failedProgress: string;
  downloadMp3: string;
  downloadSrt: string;
  downloadLrc: string;
  subtitlePreview: string;
  emptyTextError: string;
  emptyVoiceError: string;
  emptyProcessedTextError: string;
  dailyQuotaExceededError: string;
  unknownSegmentError: string;
  female: string;
  male: string;
  filenameFallback: string;
}

export const UI_MESSAGES: Record<UiLocale, UiMessages> = {
  "zh-CN": {
    appTitle: "免费超长文本转语音配音加字幕",
    themeLight: "浅色模式",
    themeDark: "深色模式",
    textPlaceholder: "在这里输入或粘贴您想要配音的文本内容。",
    settingsTitle: "配音参数设定",
    interfaceLanguage: "界面语言",
    ttsLanguage: "配音语言",
    voiceModel: "语音模型",
    speed: "语速 (Speed)",
    pitch: "音调 (Pitch)",
    volume: "音量 (Volume)",
    generateTitle: "立即生成",
    generateCopy: "一键生成自然语音与同步字幕。",
    generateButton: "生成语音",
    processingButton: "处理中...",
    resultTitle: "处理结果",
    idleStatus: "待开始",
    runningStatus: "处理中",
    successStatus: "已完成",
    errorStatus: "失败",
    progressTitle: "任务进度",
    idleProgress: "空闲",
    waitingForText: "等待输入文本",
    generatingAudio: "正在生成音频，请稍候",
    completeProgress: "音频与字幕已生成，可以试听和下载",
    failedProgress: "处理失败",
    downloadMp3: "下载 MP3",
    downloadSrt: "下载字幕",
    downloadLrc: "下载 LRC",
    subtitlePreview: "字幕预览",
    emptyTextError: "请输入要转换的文本。",
    emptyVoiceError: "请选择一个语音模型。",
    emptyProcessedTextError: "文本在预处理后为空，无法生成语音。",
    dailyQuotaExceededError: "今日可生成字数已用完，今天还剩 {remaining} / {limit} 字。",
    unknownSegmentError: "未知片段错误",
    female: "女",
    male: "男",
    filenameFallback: "配音",
  },
  "en-US": {
    appTitle: "Free Long Text to Speech with Subtitles",
    themeLight: "Light mode",
    themeDark: "Dark mode",
    textPlaceholder: "Enter or paste the text you want to turn into speech.",
    settingsTitle: "Voice Settings",
    interfaceLanguage: "Interface language",
    ttsLanguage: "Voice language",
    voiceModel: "Voice model",
    speed: "Speed",
    pitch: "Pitch",
    volume: "Volume",
    generateTitle: "Generate",
    generateCopy: "Create natural speech and synced subtitles in one step.",
    generateButton: "Generate speech",
    processingButton: "Processing...",
    resultTitle: "Result",
    idleStatus: "Ready",
    runningStatus: "Processing",
    successStatus: "Complete",
    errorStatus: "Failed",
    progressTitle: "Progress",
    idleProgress: "Idle",
    waitingForText: "Waiting for text",
    generatingAudio: "Generating audio, please wait",
    completeProgress: "Audio and subtitles are ready to preview and download",
    failedProgress: "Processing failed",
    downloadMp3: "Download MP3",
    downloadSrt: "Download subtitles",
    downloadLrc: "Download LRC",
    subtitlePreview: "Subtitle preview",
    emptyTextError: "Enter text to convert.",
    emptyVoiceError: "Select a voice model.",
    emptyProcessedTextError: "The text is empty after preprocessing.",
    dailyQuotaExceededError: "Daily text quota exceeded. {remaining} of {limit} characters remaining today.",
    unknownSegmentError: "Unknown segment error",
    female: "Female",
    male: "Male",
    filenameFallback: "voiceover",
  },
  "ja-JP": {
    appTitle: "長文テキスト音声変換と字幕生成",
    themeLight: "ライトモード",
    themeDark: "ダークモード",
    textPlaceholder: "音声化したいテキストを入力または貼り付けてください。",
    settingsTitle: "音声設定",
    interfaceLanguage: "表示言語",
    ttsLanguage: "音声言語",
    voiceModel: "音声モデル",
    speed: "速度",
    pitch: "ピッチ",
    volume: "音量",
    generateTitle: "生成",
    generateCopy: "自然な音声と同期字幕をまとめて生成します。",
    generateButton: "音声を生成",
    processingButton: "処理中...",
    resultTitle: "処理結果",
    idleStatus: "待機中",
    runningStatus: "処理中",
    successStatus: "完了",
    errorStatus: "失敗",
    progressTitle: "進行状況",
    idleProgress: "待機中",
    waitingForText: "テキスト入力待ち",
    generatingAudio: "音声を生成しています",
    completeProgress: "音声と字幕をプレビュー、ダウンロードできます",
    failedProgress: "処理に失敗しました",
    downloadMp3: "MP3 をダウンロード",
    downloadSrt: "字幕をダウンロード",
    downloadLrc: "LRC をダウンロード",
    subtitlePreview: "字幕プレビュー",
    emptyTextError: "変換するテキストを入力してください。",
    emptyVoiceError: "音声モデルを選択してください。",
    emptyProcessedTextError: "前処理後のテキストが空です。",
    dailyQuotaExceededError: "本日の文字数上限を超えました。今日は残り {remaining} / {limit} 文字です。",
    unknownSegmentError: "不明なセグメントエラー",
    female: "女性",
    male: "男性",
    filenameFallback: "voiceover",
  },
  "ko-KR": {
    appTitle: "긴 텍스트 음성 변환 및 자막 생성",
    themeLight: "라이트 모드",
    themeDark: "다크 모드",
    textPlaceholder: "음성으로 변환할 텍스트를 입력하거나 붙여넣으세요.",
    settingsTitle: "음성 설정",
    interfaceLanguage: "인터페이스 언어",
    ttsLanguage: "음성 언어",
    voiceModel: "음성 모델",
    speed: "속도",
    pitch: "피치",
    volume: "볼륨",
    generateTitle: "생성",
    generateCopy: "자연스러운 음성과 동기화된 자막을 한 번에 생성합니다.",
    generateButton: "음성 생성",
    processingButton: "처리 중...",
    resultTitle: "처리 결과",
    idleStatus: "대기 중",
    runningStatus: "처리 중",
    successStatus: "완료",
    errorStatus: "실패",
    progressTitle: "진행률",
    idleProgress: "대기",
    waitingForText: "텍스트 입력 대기 중",
    generatingAudio: "오디오를 생성하는 중입니다",
    completeProgress: "오디오와 자막을 미리 듣고 다운로드할 수 있습니다",
    failedProgress: "처리에 실패했습니다",
    downloadMp3: "MP3 다운로드",
    downloadSrt: "자막 다운로드",
    downloadLrc: "LRC 다운로드",
    subtitlePreview: "자막 미리보기",
    emptyTextError: "변환할 텍스트를 입력하세요.",
    emptyVoiceError: "음성 모델을 선택하세요.",
    emptyProcessedTextError: "전처리 후 텍스트가 비어 있습니다.",
    dailyQuotaExceededError: "오늘의 텍스트 한도를 초과했습니다. 오늘 남은 글자 수는 {remaining} / {limit}자입니다.",
    unknownSegmentError: "알 수 없는 세그먼트 오류",
    female: "여성",
    male: "남성",
    filenameFallback: "voiceover",
  },
};

export const TTS_LANGUAGE_LABELS: Record<string, Record<UiLocale, string>> = {
  "zh-CN": {
    "zh-CN": "中文",
    "en-US": "Chinese",
    "ja-JP": "中国語",
    "ko-KR": "중국어",
  },
  "en-US": {
    "zh-CN": "英语",
    "en-US": "English",
    "ja-JP": "英語",
    "ko-KR": "영어",
  },
  "ja-JP": {
    "zh-CN": "日语",
    "en-US": "Japanese",
    "ja-JP": "日本語",
    "ko-KR": "일본어",
  },
  "ko-KR": {
    "zh-CN": "韩语",
    "en-US": "Korean",
    "ja-JP": "韓国語",
    "ko-KR": "한국어",
  },
};

const languageDisplayNames = new Map<UiLocale, Intl.DisplayNames>();

export function getTtsLanguageLabel(
  group: Pick<VoiceGroup, "key" | "name">,
  uiLocale: UiLocale,
): string {
  const translatedLabel = TTS_LANGUAGE_LABELS[group.key]?.[uiLocale];
  if (translatedLabel) {
    return translatedLabel;
  }

  if (uiLocale === "zh-CN") {
    return group.name;
  }

  try {
    let displayNames = languageDisplayNames.get(uiLocale);
    if (!displayNames) {
      displayNames = new Intl.DisplayNames([uiLocale], { type: "language" });
      languageDisplayNames.set(uiLocale, displayNames);
    }

    return displayNames.of(group.key) ?? group.key;
  } catch {
    return group.key;
  }
}

export function isUiLocale(value: unknown): value is UiLocale {
  return (
    typeof value === "string" &&
    (SUPPORTED_UI_LOCALES as readonly string[]).includes(value)
  );
}

export function resolveUiLocale(languages: readonly string[]): UiLocale {
  for (const language of languages) {
    const normalized = language.toLowerCase();
    const exact = SUPPORTED_UI_LOCALES.find(
      (locale) => locale.toLowerCase() === normalized,
    );
    if (exact) {
      return exact;
    }

    const prefix = normalized.split("-")[0];
    const prefixed = SUPPORTED_UI_LOCALES.find(
      (locale) => locale.toLowerCase().split("-")[0] === prefix,
    );
    if (prefixed) {
      return prefixed;
    }
  }

  return DEFAULT_UI_LOCALE;
}

export function getAvailableVoiceGroups(
  groups: readonly VoiceGroup[],
): VoiceGroup[] {
  return groups.map((group) => ({ ...group }));
}

export function prioritizeVoiceGroupsForUiLocale(
  groups: readonly VoiceGroup[],
  uiLocale: UiLocale,
): VoiceGroup[] {
  const uiLanguage = uiLocale.split("-")[0].toLowerCase();
  const related: VoiceGroup[] = [];
  const others: VoiceGroup[] = [];

  groups.forEach((group) => {
    const groupLanguage = group.key.split("-")[0].toLowerCase();
    (groupLanguage === uiLanguage ? related : others).push(group);
  });

  return [...related, ...others];
}

export function getDefaultTtsLocale(
  groups: readonly VoiceGroup[],
  uiLocale: UiLocale,
): string {
  if (groups.some((group) => group.key === uiLocale)) {
    return uiLocale;
  }

  return groups.some((group) => group.key === DEFAULT_TTS_LOCALE)
    ? DEFAULT_TTS_LOCALE
    : groups[0]?.key ?? DEFAULT_TTS_LOCALE;
}
